import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useCampaignPageParam } from '../useCampaignPageParam';
import {
  useCampaignPageSizeParam,
  PAGE_SIZE_OPTIONS,
} from '../useCampaignPageSizeParam';
import { Plus, FileMagnifyingGlass, FileMinus, FileText } from '@phosphor-icons/react';
import { apiClient, useProfile, useMarkAnnouncementSeen } from '@discovr/common';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCampaignPillParam } from '../useCampaignPillParam';
import { mapThreadMessages } from '../../components/common/mapThreadMessages';
import { useIsTruncated } from '../../lib/useIsTruncated';
import { TruncationTooltip } from '../../components/common/TruncationTooltip';
import { ScreenReaderLabel } from '../../components/common/ScreenReaderLabel';
import { Button } from '../../components/ui/button';
import {
  createCampaignDetailApi,
  useMarkThreadRead,
  useMarkPostSeen,
  usePostRosterMessage,
  useEditRosterMessage,
  useDeleteRosterMessage,
  useRosterThread,
  useAnchoredMessages,
  useScriptDetail,
  useScriptVersions,
  useCampaignUiStore,
  useCampaignCapabilitiesStore,
  useCampaignHeaderQuery,
  campaignKeys,
  useRosterCounts,
} from '@discovr/features';
import type {
  MessageAnchor,
  RosterEntryScript,
} from '@discovr/types';
import { RosterTableShell } from './parts/RosterTableShell';
import { RosterScreenHeader } from './parts/RosterScreenHeader';
import { RosterSearchSort } from './parts/RosterSearchSort';
import {
  DataTable,
  ColumnsMenu,
  columnVisibilityView,
  type ColumnDef,
} from '../../components/common/DataTable';
import { EmptyState } from '../../components/common/EmptyState';
import { ReviewAction } from './ReviewAction';
import { EtaTimeline } from './EtaTimeline';
import { ScriptReviewPanel } from '../../components/common/ScriptReview/ScriptReviewPanel';
import { CommentIntroModal } from '../../components/common/ScriptReview/CommentIntroModal';
import { AddScriptModal } from './AddScriptModal';
import type { ScriptDoc } from '@discovr/types';
import { isThreadLockedError } from '../../components/common/notificationErrors';
import { resolveThreadLocked } from '../../components/common/resolveThreadLocked';
import type { ScriptReviewAiEnvelope } from '../../components/common/ScriptReview/types';
import { PlatformCreatorCell } from '../../lib/platformProfile';
import { StatusBadge } from './parts/StatusBadge';
import { JumpToTopPill } from './parts/JumpToTopPill';
import { rowDisplayName } from '../utils/rowDisplayName';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { useCampaignReviewDeepLink } from '../useCampaignReviewDeepLink';
import { useCampaignReviewControls } from '../useCampaignReviewControls';
import { useCampaignBucketDeepLink } from '../useCampaignBucketDeepLink';
import type { CampaignSearch } from '../search';
import {
  useRosterStatusTabs,
  useRosterTabSort,
  rosterSortStorageKey,
  useRosterColumnPreferences,
} from '../hooks';
import type { RosterPage, RosterPillDef } from '../hooks';
import { useRosterSearch } from '../hooks/useRosterSearch';
import { usePublishSharePageInfo } from '../hooks/usePublishSharePageInfo';
import { Skeleton } from '../../components/ui/skeleton';
import { usePublicView } from '../publicView';
import { usePublicPillCapture } from '../usePublicPillCapture';
import { adaptPublicRosterRowsAsScriptRows } from './ScriptsTab.publicRosterRowAdapter';

/** Stable no-op for pagination/pill callbacks in public read-only mode. */
const PUBLIC_NOOP = () => {};

// Singleton factory for the per-pill queries below.
const unifiedApi = createCampaignDetailApi();

// Archived-revision text for the Script tab's version dropdown.
const getScriptVersionText = (entryId: string, versionId: string): Promise<string> =>
  unifiedApi.getScriptVersion(entryId, versionId).then((d) => d.text ?? '');

/** "Fetch everything" sentinel for a status-scoped pill query — pagination
 *  happens client-side on top (useRosterStatusTabs / useClientTable), not
 *  via this limit. Matches the value this file already used for its
 *  search-time fetch-all path before the roster-tabs unification. */
const MAX_FETCH_LIMIT = 10000;

// Projection lives in one place now — see components/common/mapThreadMessages.

// BE owns status filtering; sub-tabs map to `?status=` values
// ('review' → script_review, 'pending' → script_pending, 'all' → no filter).
type ScriptBucket = 'all' | 'review' | 'pending';

// Last-comment bubble variants. Each branch owns bg/hover/text/border as one
// unit so a colour change can't half-apply. Background is a custom property
// (not `background`) so the hover class isn't outranked by inline style.
const LAST_COMMENT_BUBBLE_VARIANTS = {
  ours: {
    background: 'var(--background-primary-default)',
    hoverBackground: 'var(--background-primary-default-hover)',
    color: '#FFFFFF',
    borderColor: 'var(--background-primary-default)',
  },
  theirs: {
    background: 'var(--stone-100)',
    hoverBackground: 'var(--stone-200)',
    color: 'var(--muted-foreground)',
    borderColor: 'var(--stone-200)',
  },
} as const;

function getLastCommentBubbleStyle(isOurMessage: boolean): React.CSSProperties {
  const variant = LAST_COMMENT_BUBBLE_VARIANTS[isOurMessage ? 'ours' : 'theirs'];
  return {
    display: 'inline-block',
    maxWidth: 200,
    padding: '10px 16px',
    borderRadius: 'var(--radius)',
    color: variant.color,
    border: `1px solid ${variant.borderColor}`,
    fontSize: 14,
    lineHeight: '20px',
    '--bubble-bg': variant.background,
    '--bubble-hover-bg': variant.hoverBackground,
  } as React.CSSProperties;
}

function formatLastCommentTooltip(senderName: string | null | undefined, body: string): string {
  return senderName ? `${senderName}: ${body}` : body;
}

function resolveViewerSenderRole(
  canSubmitScriptOnBehalf: boolean,
  role: string | null | undefined,
): string {
  if (canSubmitScriptOnBehalf) return 'admin';
  return role ?? 'brand_owner';
}

const SCRIPT_PILLS: RosterPillDef<ScriptBucket>[] = [
  { key: 'all', label: 'All' },
  { key: 'review', label: 'Review', status: 'script_review' },
  { key: 'pending', label: 'Pending', status: 'script_pending' },
];

function truncatePreview(s: string | undefined, n: number) {
  if (!s?.trim()) return '';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

const LAST_COMMENT_PREVIEW_CHARS = 64;

/** Last-comment bubble — shows the full comment (with sender name) on hover,
 *  but only when the preview is actually cut off: either the two-line clamp
 *  overflows, or the body was JS-sliced past the preview length. Same bubble
 *  renders as a plain span (inert rows) or a button (script_review rows). */
function LastCommentCell({
  body,
  senderName,
  bubbleStyle,
  canOpenReview,
  ariaLabel,
  onOpen,
}: {
  body: string;
  senderName: string | null | undefined;
  bubbleStyle: React.CSSProperties;
  canOpenReview: boolean;
  ariaLabel: string;
  onOpen: () => void;
}) {
  const previewText = truncatePreview(body, LAST_COMMENT_PREVIEW_CHARS);
  const tooltip = formatLastCommentTooltip(senderName, body);
  const [ref, cssTruncated] = useIsTruncated<HTMLSpanElement>(previewText);
  const labelId = useId();
  const isTruncated = cssTruncated || body.trim().length > LAST_COMMENT_PREVIEW_CHARS;
  const preview = (
    <span ref={ref} className="line-clamp-2">
      {previewText}
    </span>
  );

  if (!canOpenReview) {
    return (
      <TruncationTooltip
        isTruncated={isTruncated}
        content={tooltip}
      >
        <span className="bg-(--bubble-bg)" style={bubbleStyle}>
          {preview}
        </span>
      </TruncationTooltip>
    );
  }

  return (
    <TruncationTooltip
      isTruncated={isTruncated}
      content={tooltip}
    >
      <button
        type="button"
        aria-labelledby={labelId}
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        className="bg-(--bubble-bg) hover:bg-(--bubble-hover-bg) focus-visible:ring-ring cursor-pointer text-left transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
        style={bubbleStyle}
      >
        {preview}
        <ScreenReaderLabel id={labelId}>{ariaLabel}</ScreenReaderLabel>
      </button>
    </TruncationTooltip>
  );
}

export function ScriptsTab() {
  const { campaignId } = useCampaignUiStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Public (no-login) share view. campaignId stays null in public mode, so
  // every campaignId-gated query is disabled on its own; `enabled: !isPublicView`
  // on the list queries is explicit insurance on top of that.
  const { isPublicView, grant, rosterPage } = usePublicView();
  const {
    role,
    viewerIsSuperAdmin,
    permissions,
    aiScriptReviewVersion,
    aiGeoVersion,
    confirmScriptUpload,
    handleAdminReviewScript,
    handleAdminSetEta,
  } = useCampaignCapabilitiesStore();
  const { data: campaignHeader } = useCampaignHeaderQuery(campaignId ?? '', { enabled: !isPublicView });
  const campaignPlatform = campaignHeader?.platform;

  // Pass campaignId so a sent message re-sorts the roster (recency) for the
  // sender, who is excluded from the new_message SSE.
  const postMessage = usePostRosterMessage(campaignId ?? undefined);
  const editMessage = useEditRosterMessage(campaignId ?? undefined);
  const deleteMessage = useDeleteRosterMessage(campaignId ?? undefined);
  const handleScriptThreadComment = async (
    entryId: string,
    body: string,
    anchor?: MessageAnchor,
    replyTo?: string,
  ) => {
    try {
      await postMessage.mutateAsync({ entryId, type: 'script', body, anchor, replyTo });
    } catch (err) {
      // BE returns 423 when the thread locked mid-session (the roster row's
      // locked flag is fetch-time). Flip the composer to locked immediately.
      if (isThreadLockedError(err)) {
        setThreadLockedLive(true);
        toast.error('This conversation is locked');
        if (campaignId) {
          void queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
        }
      }
      // Re-throw so ChatThread rolls back the optimistic bubble — the message
      // never sent, so leaving it displayed would be a ghost message.
      throw err;
    }
  };

  // Busy flag for admin-only actions — the capabilities callbacks are
  // fire-and-forget and don't expose isPending.
  const [scriptActionBusy, setScriptActionBusy] = useState(false);
  // Entries approved this session, so the panel stays open with a green
  // "Approved" button until manually closed.
  const [justApprovedIds, setJustApprovedIds] = useState<Set<string>>(new Set());

  const handleApproveScript = async (entryId: string) => {
    setScriptActionBusy(true);
    try {
      await handleAdminReviewScript?.(entryId, 'approve');
      setJustApprovedIds((prev) => new Set(prev).add(entryId));
      void queryClient.invalidateQueries({
        queryKey: campaignKeys.rosterTab(campaignId ?? '', 'scripts'),
      });
    } finally {
      setScriptActionBusy(false);
    }
  };

  // Role-based default: admin sees Pending first, brand sees Review first,
  // each falling back to the other bucket if the default is empty.
  const isAdminViewer = !!(permissions?.canSubmitScriptOnBehalf);
  // Start null — the cascade effect below sets the real tab once counts settle.
  // While null the component renders a skeleton so there's no wrong-tab flash.
  const [activeTab, setActiveTab] = useState<ScriptBucket | null>(null);
  const cascadeApplied = useRef(false);
  const [, setPillParam] = useCampaignPillParam('pending');

  // Sync active tab → URL pill param whenever it changes (covers both cascade
  // and user-click paths without relying on handleTabChange being called).
  useEffect(() => {
    if (activeTab !== null) {
      setPillParam(activeTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabChange = (id: ScriptBucket) => {
    cascadeApplied.current = true;
    setActiveTab(id);
  };

  // Reset tab + cascade + deep-link guards when the campaign changes, so the
  // new campaign starts fresh (also keeps StrictMode's dev double-invoke from
  // leaving the page stuck on the skeleton).
  useEffect(() => {
    cascadeApplied.current = false;
    deepLinkBucketApplied.current = '';
    deepLinkBucketAppliedKey.current = '';
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the tab to skeleton in lockstep with clearing the StrictMode one-shot cascade guards on a campaign switch; part of the deliberately ref-coupled deep-link state machine, not a simple sync.
    setActiveTab(null);

  }, [campaignId]);
  const [dialogCreatorId, setDialogCreatorId] = useState<string | null>(null);
  // URL write-side companion to useCampaignReviewDeepLink: each open/close
  // mirrors the action to ?panel=script-review&creator=... so a refresh keeps
  // the panel open and a click produces a shareable link.
  const { openReview, closeReview, setThreadOpenUrl } = useCampaignReviewControls(
    'script-review',
    setDialogCreatorId,
  );

  // Locally flips the composer to locked on a 423 send failure. Reset per
  // opened entry so a stale lock doesn't leak across rows.
  const [threadLockedLive, setThreadLockedLive] = useState(false);
  useEffect(() => {
    setThreadLockedLive(false);
  }, [dialogCreatorId]);

  // First-time inline-comments announcement — every role that can reach
  // this tab (not brand-only), popping over the page the first time THIS
  // user opens a script review. Server-side flag (GET /profile →
  // seen_announcements), not localStorage — "first time only" must hold
  // per USER across devices/browsers, not per browser profile, and must
  // survive a cleared browser. Any dismissal (Finish, × on the step cards,
  // or "Maybe later"/× on the intro) marks it seen permanently — there's no
  // "ask me again next session" tier now that the flag isn't tied to one
  // browser's storage.
  const SCRIPT_COMMENT_INTRO_KEY = 'script_comment_intro' as const;
  const { data: profile } = useProfile();
  const markAnnouncementSeen = useMarkAnnouncementSeen();
  const hasSeenCommentIntro = !!profile?.seen_announcements?.includes(SCRIPT_COMMENT_INTRO_KEY);
  // 'done' until profile has actually loaded and confirmed the flag is
  // genuinely unset — avoids a false-positive flash of the intro for a
  // user who HAS already seen it, while GET /profile is still in flight.
  const [walkthroughPhase, setWalkthroughPhase] = useState<'intro' | 'steps' | 'done'>('done');
  useEffect(() => {
    if (profile && !hasSeenCommentIntro) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- opens a one-time announcement only after the async profile confirms it has not been seen.
      setWalkthroughPhase('intro');
    }
  }, [profile, hasSeenCommentIntro]);
  // While the announcement is up, the page behind is fully inert: body
  // scroll locked, and the overlay swallows every stray click/wheel —
  // only Maybe later / Try it out / × do anything.
  useBodyScrollLock(walkthroughPhase === 'intro');
  const completeWalkthrough = useCallback(() => {
    setWalkthroughPhase('done');
    markAnnouncementSeen.mutate(SCRIPT_COMMENT_INTRO_KEY);
  }, [markAnnouncementSeen, setWalkthroughPhase]);

  // When the user opens a script-review dialog, mark the thread read
  // for them (clears the unread badge + bumps the BE cursor; next SSE
  // EventUnreadCount echo confirms 0 on their other devices). Fire
  // once per dialog open — keying the effect on dialogCreatorId only.
  const markThreadRead = useMarkThreadRead(campaignId ?? undefined);
  // Mark the current script version seen on dialog open too — clears the
  // floating "New" badge for this viewer, independent of the thread's
  // unread-message state (see ReviewAction's `seen` prop).
  const markPostSeen = useMarkPostSeen(campaignId ?? undefined);
  useEffect(() => {
    if (!dialogCreatorId) return;
    markThreadRead.mutate({ entryId: dialogCreatorId, type: 'script' });
    markPostSeen.mutate({ entryId: dialogCreatorId, phase: 'script' });
    // mutate is stable; only refire when the dialog opens for a new entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogCreatorId]);

  // Script thread messages for the open dialog. The composer's send invalidates
  // this same key, so new messages render without a manual refetch.
  const scriptThreadQuery = useRosterThread(dialogCreatorId ?? '', 'script');
  const scriptThread = useMemo(
    () => mapThreadMessages(scriptThreadQuery.data),
    [scriptThreadQuery.data],
  );
  const handleScriptThreadEdit = async (messageId: string, body: string) => {
    if (!dialogCreatorId) return;
    try {
      await editMessage.mutateAsync({ entryId: dialogCreatorId, type: 'script', messageId, body });
    } catch (err) {
      if (isThreadLockedError(err)) {
        setThreadLockedLive(true);
        toast.error('This conversation is locked');
      } else {
        toast.error('Could not edit the message');
      }
      throw err;
    }
  };
  const handleScriptThreadDelete = async (messageId: string) => {
    if (!dialogCreatorId) return;
    try {
      await deleteMessage.mutateAsync({ entryId: dialogCreatorId, type: 'script', messageId });
    } catch (err) {
      if (isThreadLockedError(err)) {
        setThreadLockedLive(true);
        toast.error('This conversation is locked');
      } else {
        toast.error('Could not delete the message');
      }
      throw err;
    }
  };
  const scriptVersionsQuery = useScriptVersions(dialogCreatorId ?? '', !!dialogCreatorId);
  // ── Server-side pagination state ──────────────────────────────────────────
  const [page, setPage] = useCampaignPageParam();
  const [pageSize, setPageSize] = useCampaignPageSizeParam();

  // Bootstraps to 'all' while activeTab is null (pre-cascade). Public mode
  // pins the bucket to the grant's pill — only that one pill has real row data,
  // and pinning also bypasses the cascade (which never fires for a disabled query).
  const effectiveActiveTab: ScriptBucket =
    isPublicView && grant?.pill ? (grant.pill as ScriptBucket) : (activeTab ?? 'all');

  // Clicking a non-granted pill prompts sign-in with the click captured as a
  // resumable action; clicking the granted pill stays a no-op.
  const handlePublicPillClick = usePublicPillCapture('scripts', effectiveActiveTab);

  // Active bucket total from /roster/counts — drives the hybrid search decision.
  // Disabled in public mode: a public link never carries campaign-wide counts.
  const { data: countsForSearch } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const activeScriptTotal = !countsForSearch
    ? undefined
    : effectiveActiveTab === 'review'
      ? countsForSearch.phase.by_status.script_review
      : effectiveActiveTab === 'pending'
        ? countsForSearch.phase.by_status.script_pending
        : countsForSearch.phase.script;
  // Stable identity so the search-reset effect dep does not churn every render.
  const resetPageForSearch = useCallback(() => setPage(1), [setPage]);
  const searchState = useRosterSearch(activeScriptTotal, pageSize, {
    onQueryChange: resetPageForSearch,
  });

  const getRowSearchText = useCallback((c: RosterEntryScript) => {
    const cAny = c as unknown as {
      creator?: { name?: string };
      platform?: { username?: string; name?: string };
    };
    return [cAny.creator?.name ?? '', cAny.platform?.username ?? '', cAny.platform?.name ?? ''].join(' ');
  }, []);

  // Per-tab column sort (persisted), threaded into the query below. Creator is
  // the only sortable column here (matches the BE scriptContentSortFields).
  const rosterSort = useRosterTabSort<ScriptBucket>(rosterSortStorageKey('scripts', campaignId), effectiveActiveTab);

  // Server-side pagination: only the active pill fetches a page; badges come
  // from /roster/counts. Searching falls back to the whole bucket + local filter.
  const buildPageQuery = useCallback(
    (pill: RosterPillDef<ScriptBucket>, pageNum: number, size: number) => {
      const cid = campaignId ?? '';
      // One source for the sort params, spread into the query key AND the call so
      // the two never diverge — and so the search path sorts the bucket too (else
      // the header shows an active sort while the rows come back in default order).
      const sortParams = rosterSort.sortParamsFor(pill.key);
      // No server-side ?search= for scripts, so a searched bucket fetches once
      // and client-filters; small buckets take the normal paged path.
      if (searchState.serverSearch) {
        return {
          // Key by (pill, search, sort); `select` slices the visible page from the cache.
          queryKey: campaignKeys.rosterTab(cid, 'scripts', {
            status: pill.status,
            phase: `${pill.key}:bucket`,
            search: searchState.serverSearch,
            limit: MAX_FETCH_LIMIT,
            ...sortParams,
          }),
          queryFn: async () => {
            const res = await unifiedApi.listScript(cid, {
              status: pill.status,
              page: 1,
              limit: MAX_FETCH_LIMIT,
              ...sortParams,
            });
            const q = (searchState.serverSearch ?? '').toLowerCase();
            const rows = res.entries.filter((c) => getRowSearchText(c).toLowerCase().includes(q));
            return { entries: rows, total: rows.length, totalPages: 1 };
          },
          select: (bucket: RosterPage<RosterEntryScript>): RosterPage<RosterEntryScript> => {
            const start = (pageNum - 1) * size;
            return {
              entries: bucket.entries.slice(start, start + size),
              total: bucket.total,
              totalPages: Math.max(1, Math.ceil(bucket.total / size)),
            };
          },
          staleTime: 30_000,
          // Real, independently-honored gate (ANDed with !!campaignId, not clobbered).
          enabled: !isPublicView,
        };
      }
      return {
        queryKey: campaignKeys.rosterTab(cid, 'scripts', {
          status: pill.status,
          page: pageNum,
          limit: size,
          ...sortParams,
        }),
        enabled: !isPublicView,
        queryFn: () =>
          unifiedApi
            .listScript(cid, { status: pill.status, page: pageNum, limit: size, ...sortParams })
            .then((res) => ({ entries: res.entries, total: res.total, totalPages: res.totalPages })),
        staleTime: 30_000,
      };
    },
    [campaignId, searchState.serverSearch, getRowSearchText, isPublicView, rosterSort],
  );

  // Disabled in public mode: a public link never carries campaign-wide
  // counts, and inactive pills' badges must show a skeleton, not a false 0.
  const { data: rosterCounts } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const scriptCounts = useMemo<Partial<Record<ScriptBucket, number>>>(
    () => ({
      all: rosterCounts?.phase.script ?? 0,
      review: rosterCounts?.phase.by_status.script_review ?? 0,
      pending: rosterCounts?.phase.by_status.script_pending ?? 0,
    }),
    [rosterCounts],
  );

  const {
    pills: scriptPills,
    pageRows: filtered,
    activeRows,
    total,
    pageCount,
    isLoading: isInitialLoading,
    isError: isScriptQueryError,
    isRefetching: isLoadingEntries,
  } = useRosterStatusTabs<RosterEntryScript, ScriptBucket>({
    campaignId: campaignId || undefined,
    activeTab: effectiveActiveTab,
    pills: SCRIPT_PILLS,
    buildPageQuery,
    counts: scriptCounts,
    pageSize,
    page,
    setPage,
    clientFilter: searchState.clientQuery
      ? (r) => getRowSearchText(r).toLowerCase().includes(searchState.clientQuery)
      : undefined,
  });

  // Publishes pageSize/pageCount for the admin-header share button. Disabled in
  // public mode: that button doesn't exist there and pageCount would be wrong.
  usePublishSharePageInfo(pageSize, pageCount, !isPublicView);

  const pendingCount = scriptPills.find((p) => p.key === 'pending')?.count ?? 0;
  const reviewCount = scriptPills.find((p) => p.key === 'review')?.count ?? 0;
  const allCount = scriptPills.find((p) => p.key === 'all')?.count ?? 0;

  // Public mode's actual data source: the granted pill's rows from the public
  // payload, adapted to the script-row shape (`filtered` above stays empty).
  const publicRows = useMemo(
    () => adaptPublicRosterRowsAsScriptRows(rosterPage?.rows ?? []),
    [rosterPage],
  );
  const displayedRows = isPublicView ? publicRows : filtered;

  // Pill badges: every pill except the granted one shows a skeleton (a public
  // link carries no other pill's count). The granted pill shows its row count
  // only when it's a single page; multi-page falls back to 'loading'.
  const displayPills = useMemo(() => {
    if (!isPublicView) return scriptPills;
    const grantedPillKey = grant?.pill;
    const grantedTotalPages = rosterPage?.total_pages ?? grant?.totalPages ?? 1;
    const grantedRowCount = rosterPage?.rows.length ?? 0;
    return scriptPills.map((p) => ({
      ...p,
      count:
        p.key === grantedPillKey
          ? grantedTotalPages > 1
            ? ('loading' as const)
            : grantedRowCount
          : ('loading' as const),
    }));
  }, [scriptPills, isPublicView, grant?.pill, grant?.totalPages, rosterPage]);

  const [addScriptModalOpen, setAddScriptModalOpen] = useState<string | null>(null);
  const [addScriptLoading, setAddScriptLoading] = useState(false);

  // How many comments are anchored to the CURRENT script version, for the
  // Add Script modal's own "previous version had N comments" line — purely
  // informational (comments are reference tags, not a blocker; see
  // AddScriptModal's own doc comment). Two chained queries: the version_id
  // isn't on the roster row itself, only its numeric `version`, so this
  // resolves the live script detail first, then counts anchors for that
  // version. Both no-op until the modal is actually open.
  const addScriptDetailQuery = useScriptDetail(addScriptModalOpen ?? '', 0);
  const addScriptAnchorsQuery = useAnchoredMessages(
    addScriptModalOpen ?? '',
    'script',
    addScriptDetailQuery.data?.version_id ?? '',
  );

  // Combine the script-review and GEO SSE counters: a bump on EITHER forces a
  // fresh script-detail fetch (both are per-entry, monotonically increasing).
  const dialogReviewVersion = dialogCreatorId
    ? (aiScriptReviewVersion?.[dialogCreatorId] ?? 0) + (aiGeoVersion?.[dialogCreatorId] ?? 0)
    : 0;

  // On dialog open (or an SSE review/GEO bump for this entry), refetch the
  // authoritative script + AI envelope. Empty dialogCreatorId disables the query.
  const scriptDetailQuery = useScriptDetail(dialogCreatorId ?? '', dialogReviewVersion);
  const scriptDetail = scriptDetailQuery.data ?? null;
  const scriptDetailLoading = scriptDetailQuery.isLoading;

  // Full, unpaginated anchor set for the current script version — backs the
  // Script tab's persistent highlights independently of scriptThread's
  // chat-pagination window (see ScriptReviewPanel's openAnchorMessages prop).
  const anchoredMessagesQuery = useAnchoredMessages(
    dialogCreatorId ?? '',
    'script',
    scriptDetail?.version_id ?? '',
  );
  // undefined (not []) while the query hasn't successfully resolved yet —
  // ScriptReviewPanel's openAnchors falls back to filtering `thread` on
  // undefined, via `??`. mapThreadMessages always returns [] even for a
  // missing page, so that fallback has to happen HERE, before mapping,
  // or a still-loading (or failed) query would look identical to "loaded,
  // zero anchors" and silently blank the highlights instead of falling back.
  const openAnchorMessages = useMemo(
    () =>
      anchoredMessagesQuery.data
        ? mapThreadMessages({ messages: anchoredMessagesQuery.data })
        : undefined,
    [anchoredMessagesQuery.data],
  );

  // GEO pack generation trigger (YouTube only). Enqueues async; the pack lands
  // via the geo webhook → SSE bump → refetch. The busy flag is keyed to the
  // entry id so opening another entry mid-request doesn't disable its button.
  const [geoBusyId, setGeoBusyId] = useState<string | null>(null);
  const handleGenerateGeo = async (entryId: string) => {
    setGeoBusyId(entryId);
    try {
      await apiClient.post(`/roster/${encodeURIComponent(entryId)}/geo/generate`);
      await scriptDetailQuery.refetch();
    } catch (err) {
      // May fire before the script is approved (409) — don't throw; the tab
      // falls back to a manual button.
      console.warn('GEO generate skipped:', err);
    } finally {
      // Only clear if this entry is still the busy one, so a mid-flight switch
      // doesn't clear the new entry's spinner.
      setGeoBusyId((cur) => (cur === entryId ? null : cur));
    }
  };

  // GEO regenerate (YouTube only). Synchronous on the BE: re-generates the whole
  // pack (element '') or a single element and returns the fresh pack, so a
  // refetch converges the tab. Errors are caught and logged, not re-thrown
  // (the caller only resets its spinner), matching the other GEO handlers.
  const handleRegenerateGeo = async (entryId: string, element: string) => {
    try {
      await apiClient.post(`/roster/${encodeURIComponent(entryId)}/geo/regenerate`, { element });
      await scriptDetailQuery.refetch();
    } catch (err) {
      console.warn('GEO regenerate failed:', err);
    }
  };

  // GEO per-element selection auto-save. The persisted set is surfaced back on
  // the geo envelope so the tab restores it on reopen. Fire-and-forget per
  // toggle; errors are logged so a transient failure doesn't break the toggle.
  const handleSaveGeoSelection = async (entryId: string, selected: string[]) => {
    try {
      await apiClient.post(`/roster/${encodeURIComponent(entryId)}/geo/selection`, { selected });
    } catch (err) {
      console.warn('GEO selection save failed:', err);
    }
  };

  // Poll-while-pending backstop: SSE is at-most-once (user may be reconnecting),
  // so while the open dialog's GEO pack is pending, poll the detail every 5s so
  // the tab still converges on the persisted result if the nudge is dropped.
  const geoStatus = scriptDetail?.geo_analysis?.status;
  const geoPending = !!dialogCreatorId && (geoStatus === 'pending' || geoStatus === 'processing');
  const refetchScriptDetailRef = useRef(scriptDetailQuery.refetch);
  // Keep the latest refetch in a ref for the interval callback (written in an
  // effect so we never mutate a ref during render).
  useEffect(() => {
    refetchScriptDetailRef.current = scriptDetailQuery.refetch;
  });
  useEffect(() => {
    if (!geoPending) return;
    const id = setInterval(() => {
      void refetchScriptDetailRef.current();
    }, 5000);
    return () => clearInterval(id);
  }, [geoPending]);


  // URL deep-link discriminator, lifted above the cascade so the cascade-default
  // effect can defer when a deep link is in play.
  const { panel: searchPanel, creator: searchCreator } = useSearch({
    strict: false,
  }) as Partial<CampaignSearch>;
  const isScriptDeepLink = searchPanel === 'script-review' && !!searchCreator;

  // One-time fallback once all three pills settle: if the role-default bucket
  // is empty, switch to the other one. Guarding on isInitialLoading (not the
  // counts) avoids a spurious switch when one pill resolves before another.
  // Defers to the script-review deep link when present.
  useEffect(() => {
    // Public mode pins the bucket directly and this cascade never fires there;
    // guarded explicitly so it can't write stray state.
    if (isPublicView) return;
    if (cascadeApplied.current) return;
    if (isInitialLoading) return;
    if (isScriptDeepLink) return;
    cascadeApplied.current = true;
    // Always set a real tab value, firing the component out of skeleton.
    if (isAdminViewer) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot role-default cascade (guarded by cascadeApplied ref, deferred until pills settle and no deep link is active); fires the tab out of skeleton exactly once, not a prop→state sync.
      setActiveTab(pendingCount > 0 ? 'pending' : reviewCount > 0 ? 'review' : 'pending');
    } else {
      setActiveTab(reviewCount > 0 ? 'review' : pendingCount > 0 ? 'pending' : 'review');
    }
  }, [isInitialLoading, pendingCount, reviewCount, isAdminViewer, isScriptDeepLink, isPublicView]);

  const handleAddScript = async (
    creatorId: string,
    rosterEntryId: string,
    doc: ScriptDoc,
  ) => {
    try {
      setAddScriptLoading(true);
      await confirmScriptUpload?.(rosterEntryId, doc);
      setAddScriptModalOpen(null);
    } finally {
      setAddScriptLoading(false);
    }
  };

  // Cache the open dialog's row on sight. Under server-side pagination the row
  // can leave `activeRows` (paging, a recency refetch) while its dialog is open,
  // so re-deriving would strand the panel.
  const [cachedDialogRow, setCachedDialogRow] = useState<RosterEntryScript | null>(null);
  const rowOnPage = dialogCreatorId
    ? activeRows.find((x) => x.id === dialogCreatorId) ?? null
    : null;
  // Adjust the cache while rendering (guarded against looping): refresh when the
  // row is on-page, clear when the dialog closes.
  if (rowOnPage && rowOnPage !== cachedDialogRow) {
    setCachedDialogRow(rowOnPage);
  } else if (!dialogCreatorId && cachedDialogRow !== null) {
    setCachedDialogRow(null);
  }
  // Prefer the freshest copy on the page; fall back to the cached one.
  const dc: RosterEntryScript | null = dialogCreatorId
    ? rowOnPage ?? cachedDialogRow
    : null;
  // Single source of truth for this thread's lock: the composer gate and the
  // Edit/Delete gate must never be able to drift apart.
  const scriptThreadLocked = resolveThreadLocked(
    scriptThreadQuery.data?.locked,
    dc?.script_thread_locked ?? false,
    threadLockedLive,
  );

  // Close the review panel on pill change only — not on "row missing from
  // activeRows", which under pagination would slam it shut on every page change.
  const prevPillForDialogRef = useRef(effectiveActiveTab);
  useEffect(() => {
    if (prevPillForDialogRef.current === effectiveActiveTab) return;
    prevPillForDialogRef.current = effectiveActiveTab;
    // Just-approved rows keep their panel open (green confirmation state).
    if (dialogCreatorId && !justApprovedIds.has(dialogCreatorId)) closeReview();
  }, [effectiveActiveTab, dialogCreatorId, closeReview, justApprovedIds]);

  // ── Deep link: ?panel=script-review&creator=<username>&thread=open ──────────
  // One-way (URL -> state). Review-able rows live in the 'review' bucket, so a
  // deep link must (a) switch the bucket to 'review' so the target loads, then
  // (b) resolve the username against those rows and open the dialog.
  const { creatorId: deepLinkCreatorId } =
    useCampaignReviewDeepLink(activeRows, 'script-review');

  // (a) Force the 'review' bucket once per deep-link target so the row loads,
  // marking the cascade applied so it doesn't bounce back. `deepLinkBucketKey`
  // resets the guard when the campaign or deep-linked username changes.
  const deepLinkBucketKey = `${campaignId ?? ''}|${isScriptDeepLink ? searchCreator : ''}`;
  const deepLinkBucketApplied = useRef('');
  useEffect(() => {
    // Public mode never resolves a deep link; opening a dialog here would fire
    // a real network request.
    if (isPublicView) return;
    if (!isScriptDeepLink) return;
    if (deepLinkBucketApplied.current === deepLinkBucketKey) return;
    // eslint-disable-next-line react-hooks/immutability -- one-shot guard ref for the deep-link bucket force; deliberately mutated here (and reset in the campaign-switch effect above) to make the StrictMode double-invoke idempotent.
    deepLinkBucketApplied.current = deepLinkBucketKey;
    cascadeApplied.current = true;
    setActiveTab('review');
  }, [isScriptDeepLink, deepLinkBucketKey, campaignId, isPublicView]);

  // (b) Once the target resolves against the loaded 'review' entries, open it.
  useEffect(() => {
    if (isPublicView) return;
    if (!deepLinkCreatorId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- opens the dialog once the deep-link target resolves against the loaded review rows; functional updater is a no-op when already open, part of the ref-coupled deep-link machine, not a prop→state sync.
    setDialogCreatorId((id) => (id === deepLinkCreatorId ? id : deepLinkCreatorId));
  }, [deepLinkCreatorId, isPublicView]);

  // ── Deep link: ?tab=scripts&pill=<bucket> ───────────────────────────────────
  // One-way (URL -> state). Restores the list bucket from a plain tab share link
  // (returns null under a script-review deep link, so review-forcing wins).
  // Ref-guarded so it doesn't fight manual pill clicks.
  const deepLinkBucket = useCampaignBucketDeepLink('scripts');
  const deepLinkBucketAppliedKey = useRef('');
  useEffect(() => {
    if (isPublicView) return;
    if (!deepLinkBucket) return;
    const key = `${campaignId ?? ''}|${deepLinkBucket}`;
    if (deepLinkBucketAppliedKey.current === key) return;
    // eslint-disable-next-line react-hooks/immutability -- one-shot guard ref for the bucket deep-link restore; deliberately mutated here (and reset in the campaign-switch effect above) to make the StrictMode double-invoke idempotent.
    deepLinkBucketAppliedKey.current = key;
    cascadeApplied.current = true;
    setActiveTab(deepLinkBucket);
  }, [deepLinkBucket, campaignId, isPublicView]);

  // Server-persisted column visibility (Columns dropdown) — holds per user
  // across devices/browsers; see useColumnPreferences.
  const columnState = useRosterColumnPreferences(campaignId, 'scripts');

  // Column definitions — memoized (like the other roster tabs) and built ABOVE
  // the early return so the hooks run unconditionally (Rules of Hooks);
  // `permissions` is read only via optional chaining, so construction is safe
  // while permissions is still null.
  const columns = useMemo<ColumnDef<RosterEntryScript>[]>(() => {
    return [
      {
        id: 'creator',
        alwaysVisible: true,
        header: 'Creator',
        // Matches Creators/Confirmed's Creator column exactly.
        minWidth: 320,
        maxWidth: 340,
        grow: true,
        sticky: 'left',
        // The one sortable column on this basic-info tab (server-side, by display
        // name). Disabled in public view, like the other roster tabs.
        sortable: !isPublicView,
        cell: (c) => (
          <PlatformCreatorCell
            creator={c as unknown as Record<string, unknown>}
            campaignPlatform={campaignPlatform}
          />
        ),
      },
      {
        id: 'status',
        alwaysVisible: true,
        header: 'Status',
        // Fixed 145 — matches ContentTab so the shared layout stays aligned.
        minWidth: 145,
        maxWidth: 145,
        cell: (c) => {
          // Permanent skeleton in public mode: the public wire carries no
          // script-review status, so there's nothing real to show.
          if (isPublicView) return <Skeleton className="h-6 w-24 rounded-full" />;
          return <StatusBadge status={c.status ?? 'unknown'} />;
        },
      },
      {
        id: 'last_comment',
        header: 'Last comment',
        // Floor must exceed the bubble's own maxWidth (200) plus cell padding,
        // or the column could shrink narrower than its content.
        minWidth: 216,
        maxWidth: 224,
        wrap: true,
        cell: (c) => {
          // Permanent skeleton in public mode — the public wire carries no `last_message`.
          if (isPublicView) return <Skeleton className="h-8 w-40 rounded-full" />;
          const lastMsg = c.last_message;
          if (!lastMsg?.body) return <p className="text-sm text-muted-foreground">—</p>;
          // Sender-aware bubble: dark for our messages, light for received.
          const viewerSenderRole = resolveViewerSenderRole(
            !!permissions?.canSubmitScriptOnBehalf,
            role,
          );
          const isOurMessage = lastMsg.sender_role === viewerSenderRole;
          // Only script_review rows resolve the review deep link; others stay
          // inert rather than writing a URL that bounces the pill and closes itself.
          const canOpenReview = c.status === 'script_review';
          return (
            <LastCommentCell
              body={lastMsg.body}
              senderName={lastMsg.sender_name}
              bubbleStyle={getLastCommentBubbleStyle(isOurMessage)}
              canOpenReview={canOpenReview}
              ariaLabel={`Open script review comments for ${rowDisplayName(c)}`}
              onOpen={() => {
                if (dialogCreatorId === c.id) closeReview();
                else openReview(c);
              }}
            />
          );
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        // Owns the row's whole time story: when it last changed, and — via
        // EtaTimeline — when the next one (delivery or revision) is expected.
        minWidth: 150,
        maxWidth: 150,
        cell: (c) => {
          // Permanent skeleton in public mode — the public wire carries no `status_updated_at`.
          if (isPublicView) return <Skeleton className="h-4 w-24" />;
          const canEditEta =
            !!permissions?.canSubmitScriptOnBehalf && typeof handleAdminSetEta === 'function';
          return (
            <EtaTimeline
              status={c.status ?? ''}
              phase="script"
              eta={c.script_eta ?? null}
              updatedAt={c.script_updated_at}
              canEditEta={canEditEta}
              onSetEta={(iso) => handleAdminSetEta?.(c.id, 'script', iso)}
            />
          );
        },
      },
      {
        id: 'action',
        alwaysVisible: true,
        header: 'Action',
        // Role-aware fixed width (pinned both ends so it doesn't shift between
        // pending/review pills). Admin's left slot holds Review (+ the v2/New
        // tags once a revision has landed) and the right slot the "Add script"
        // button; brand only ever sees Review. The ETA itself lives in the
        // Updated column (EtaTimeline), not here.
        minWidth: permissions?.canSubmitScriptOnBehalf ? 260 : 140,
        maxWidth: permissions?.canSubmitScriptOnBehalf ? 260 : 140,
        cell: (c) => {
          // Permanent skeleton in public mode — no interactive action is rendered
          // here (read-only permissions, and no per-row script-review status).
          if (isPublicView) return <Skeleton className="h-8 w-16 rounded-full" />;
          const hasAddScript = permissions?.canSubmitScriptOnBehalf;
          return (
            // Review + Add script sit together on the left with a tight gap; the
            // column is fixed wide enough for Review's v2/New tags, so any extra
            // space stays as trailing room on the right rather than a gap between
            // the two buttons.
            <div className="flex items-center justify-start gap-2">
              <div className="flex items-center">
                <ReviewAction
                  status={c.status ?? ''}
                  phase="script"
                  version={c.version}
                  unreadCount={c.unread_count}
                  hasEta={!!c.script_eta}
                  seen={c.script_seen}
                  onToggleReview={() => (dialogCreatorId === c.id ? closeReview() : openReview(c))}
                />
              </div>
              <div className="flex items-center">
                {hasAddScript && (
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => setAddScriptModalOpen(c.id)}
                    className="h-8 gap-1 rounded-full px-3 text-caption-medium hover:bg-(--brand-50) hover:text-(--brand-500)"
                    style={{ borderColor: '#e5e5e5' }}
                  >
                    <Plus size={14} weight="bold" /> Add script
                  </Button>
                )}
              </div>
            </div>
          );
        },
      },
    ];
    // Action column stays visible on every sub-tab; its cell logic gates Review
    // and Add script per row (so admin can add a script for a creator who hasn't).
  }, [
    isPublicView,
    permissions,
    role,
    campaignPlatform,
    handleAdminSetEta,
    dialogCreatorId,
    closeReview,
    openReview,
    setAddScriptModalOpen,
  ]);

  const columnView = useMemo(
    () => columnVisibilityView(columns, columnState.hiddenIds),
    [columns, columnState.hiddenIds],
  );

  // Permissions gate lives below all hooks so hook call order stays stable
  // (Rules of Hooks); `permissions` is only read above via optional chaining.
  if (!permissions) return null;

  // Contextual empty state — global (nothing approved) vs per-filter copy.
  const emptyState =
    !isLoadingEntries && allCount === 0 ? (
      <EmptyState
        icon={FileText}
        title="No approved creators yet"
        body="Creators appear here once the brand approves them."
      />
    ) : effectiveActiveTab === 'review' ? (
      <EmptyState
        icon={FileMagnifyingGlass}
        title="No scripts to review"
        body="Scripts creators submit for your approval show here."
      />
    ) : effectiveActiveTab === 'pending' ? (
      <EmptyState
        icon={FileMinus}
        title="No scripts pending"
        body="Scripts creators submit for your approval show here."
      />
    ) : (
      <EmptyState
        icon={FileText}
        title="No scripts yet"
        body="Scripts start once your creators are finalized. Each one drafts a script, our Script Review agent checks it for brand fit and compliance, then it comes to you."
        action={{
          label: 'View finalized creators',
          onClick: () =>
            navigate({
              to: '.',
              search: ((p: Partial<CampaignSearch>) => ({ ...p, tab: 'confirmed', pill: undefined })) as never,
            }),
        }}
      />
    );

  // Public-mode pagination display from the grant snapshot (the hook's own
  // values are disabled/wrong). The `total_pages > 1` condition drives both
  // hideSummary and the granted pill's badge — they must never disagree.
  const publicPageSize = grant?.pageSize ?? pageSize;
  const publicGrantedRowCount = rosterPage?.rows.length ?? 0;
  const displayPage = isPublicView ? (rosterPage?.page ?? grant?.page ?? 1) : page;
  const displayTotalPages = isPublicView
    ? (rosterPage?.total_pages ?? grant?.totalPages ?? 1)
    : pageCount;
  const hideSummary = isPublicView && displayTotalPages > 1;
  const displayTotal = isPublicView
    ? displayTotalPages > 1
      ? Math.max(publicGrantedRowCount, 1)
      : publicGrantedRowCount
    : total;

  return (
    <>
      <JumpToTopPill isRefetching={isLoadingEntries} page={page} onJump={() => setPage(1)} />
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <RosterScreenHeader title="Scripts" subtitle="See what your creators have to say." />
        <RosterTableShell<ScriptBucket>
          fill
          activeTab={effectiveActiveTab}
          // Public mode prompts sign-in (click captured for resume) instead of
          // switching pills directly — see handlePublicPillClick.
          onTabChange={isPublicView ? handlePublicPillClick : handleTabChange}
          // Search has no meaning against a single fetched page in public mode,
          // so it's hidden entirely rather than wired to a no-op.
          controls={
            isPublicView ? undefined : (
              <RosterSearchSort
                query={searchState.query}
                onQueryChange={searchState.setQuery}
                placeholder="Search scripts…"
                columnsMenu={
                  <ColumnsMenu
                    items={columnView.menuItems}
                    hiddenCount={columnView.hiddenCount}
                    onToggle={columnState.toggle}
                    onReset={columnState.reset}
                  />
                }
              />
            )
          }
          // Public mode never shows the whole-tab skeleton — displayPills
          // already carries a per-pill 'loading' sentinel for every pill
          // except the granted one.
          tabsLoading={isPublicView ? false : activeTab === null}
          tabs={displayPills}
          page={displayPage}
          totalPages={displayTotalPages}
          total={displayTotal}
          // Never a fabricated campaign-wide total — see displayTotal above.
          hideSummary={hideSummary}
          onPage={isPublicView ? PUBLIC_NOOP : setPage}
          pageSize={isPublicView ? publicPageSize : pageSize}
          pageSizeOptions={isPublicView ? undefined : PAGE_SIZE_OPTIONS}
          onPageSizeChange={isPublicView ? undefined : setPageSize}
        >
          {isScriptQueryError ? (
            <p className="py-16 text-center text-sm text-rose-500">
              Couldn't load scripts. Please refresh.
            </p>
          ) : (
            <DataTable<RosterEntryScript>
              fillHeight
              columns={columnView.visibleColumns}
              data={displayedRows}
              getRowId={(c) => c.id}
              sort={rosterSort.sortProp(effectiveActiveTab, { disabled: isPublicView, onChange: () => setPage(1) })}
              // isInitialLoading would never settle in public mode, but the
              // granted rows are already available, so render them immediately.
              loading={isPublicView ? false : isInitialLoading || activeTab === null}
              emptyState={emptyState}
              className="rounded-none border-0 bg-transparent"
            />
          )}
        </RosterTableShell>
      </div>

      {/* Admin / brand: full review panel with thread + approve */}
      {dc && role != null ? (() => {
        const c = dc as unknown as Record<string, unknown>;
        const envelope =
          scriptDetailLoading && !scriptDetail
            ? ({ status: 'pending' } as ScriptReviewAiEnvelope)
            : (scriptDetail?.ai_analysis as ScriptReviewAiEnvelope | undefined);

        return (
          <ScriptReviewPanel
            // Remount per entry so GeoTab's selection/auto-fire refs never leak
            // across a direct A→B creator switch.
            key={dc.id}
            open
            onClose={closeReview}
            onThreadOpenChange={setThreadOpenUrl}
            title="Script review"
            creatorName={rowDisplayName(dc)}
            creatorAvatarUrl={
              (c.platform as Record<string,unknown>|undefined)?.profile_picture_url as string|undefined ??
              (c.creator as Record<string,unknown>|undefined)?.profile_pic as string|undefined
            }
            creatorHandle={
              (c.platform as Record<string,unknown>|undefined)?.name as string|undefined ??
              (c.platform as Record<string,unknown>|undefined)?.username as string|undefined
            }
            shareCreatorUsername={
              (c.platform as Record<string,unknown>|undefined)?.username as string|undefined
            }
            statusLabel={(dc.status as string)?.replace(/_/g, ' ')}
            statusTone={
              (dc.status as string) === 'script_approved' ? 'approved'
                : (dc.status as string) === 'script_revision_requested' ? 'revision'
                  : (dc.status as string) === 'script_pending' ? 'pending'
                    : 'default'
            }
            uploadedLabel={scriptDetail?.uploaded_at}
            versionLabel={scriptDetail?.version ? `v${scriptDetail.version}` : undefined}
            assignedProducts={scriptDetail?.assigned_products}
            campaignProductCount={campaignHeader?.products?.length ?? 0}
            doc={scriptDetail?.doc}
            scriptTextLoading={scriptDetailLoading}
            scriptPdfUrl={scriptDetail?.pdf_url ?? (c.script_file_url as string | undefined)}
            aiAnalysis={envelope}
            geoAnalysis={scriptDetail?.geo_analysis}
            showGeo={(campaignPlatform ?? '').toLowerCase().includes('youtube')}
            onGenerateGeo={() => void handleGenerateGeo(dc.id)}
            geoGenerating={geoBusyId === dc.id}
            onRegenerateGeo={(element) => handleRegenerateGeo(dc.id, element)}
            onSaveGeoSelection={(selected) => void handleSaveGeoSelection(dc.id, selected)}
            thread={scriptThread}
            openAnchorMessages={openAnchorMessages}
            unreadCount={(dc as { unread_count?: number }).unread_count ?? 0}
            viewerActor={role ?? 'brand_owner'}
            canAddComment={!scriptThreadLocked}
            locked={scriptThreadLocked}
            addCommentBusy={postMessage.isPending}
            // Return the promise (not void-wrapped) so ChatThread's submit()
            // actually awaits it and can roll back the optimistic bubble +
            // restore the draft on a failed send (see handleScriptThreadComment).
            onAddComment={({ body, anchor, replyTo }) => handleScriptThreadComment(dc.id, body, anchor, replyTo)}
            onEditMessage={handleScriptThreadEdit}
            onDeleteMessage={handleScriptThreadDelete}
            viewerIsSuperAdmin={viewerIsSuperAdmin}
            scriptVersionId={scriptDetail?.version_id}
            scriptVersions={scriptVersionsQuery.data?.versions}
            onFetchVersionText={(versionId) =>
              getScriptVersionText(dc.id, versionId)
            }
            // Line-by-Line's "Add to Comment" only makes sense while the
            // script is still actively under review — once it's moved on
            // (script_approved, content_pending, …) a stale AI suggestion
            // shouldn't be appendable to a review that's already over.
            canAddLineComment={dc.status === 'script_review'}
            canReview={!!permissions.canApproveScript && dc.status === 'script_review'}
            reviewBusy={!!scriptActionBusy}
            isApproved={justApprovedIds.has(dc.id)}
            onApprove={() => void handleApproveScript(dc.id)}
            walkthroughActive={walkthroughPhase === 'steps'}
            onWalkthroughDone={completeWalkthrough}
            threadDefaultOpen
          />
        );
      })() : null}

      {/* Announcement modal — self-portals via Radix Dialog (focus trap +
          Escape + aria-modal); outside clicks stay inert by design. */}
      {walkthroughPhase === 'intro' && dc ? (
        <CommentIntroModal
          onTryIt={() => setWalkthroughPhase('steps')}
          onDismiss={completeWalkthrough}
        />
      ) : null}
      {addScriptModalOpen && (() => {
        const creator = activeRows.find((c) => c.id === addScriptModalOpen);
        if (!creator) return null;
        return (
          <AddScriptModal
            key={creator.id}
            open={true}
            onClose={() => setAddScriptModalOpen(null)}
            creatorName={rowDisplayName(creator)}
            creatorId={addScriptModalOpen}
            rosterEntryId={creator.id}
            onScriptSubmit={handleAddScript}
            isLoading={addScriptLoading}
            previousCommentCount={addScriptAnchorsQuery.data?.length}
          />
        );
      })()}
    </>
  );
}
